<template>
  <article
    ref="tile"
    class="flex relative"
    :class="[
      component?.main ?? '',
      verticalAlign ? 'items-center' : ''
    ]"
    :data-testid="dataTestid"
  >
    <div
      ref="tileImageWrapper"
      class="tile-image flex items-start relative"
    >
      <AtomsImage
        ref="tileImage"
        class="object-cover flex-none"
        :src="`${image}?w=${component.image?.width}&h=${component.image?.height}`"
        :width="component.image?.width"
        :height="component.image?.height"
        :class="component.image?.class"
        :alt="imageAlt || title"
        :sizes="component?.image?.sizes"
        preload
      />

      <div
        v-if="logo && layout !== 'small-tile'"
        class="absolute bottom-4 left-4 w-12 h-12 rounded-lg bg-white shadow-lg flex justify-center items-center p-[0.375rem]"
      >
        <AtomsImage
          ref="tileImage"
          class="object-cover flex-none max-w-full max-h-full"
          :src="logo"
          width="45"
          height="45"
          :alt="logoAlt || logo"
          :image-lazy-load="imageLazyLoad"
          preload
        />
      </div>
    </div>

    <div
      ref="details"
      class="flex flex-col flex-grow items-start"
      :class="component.details"
    >
      <p
        v-if="tag && layout !== 'small-tile' && type === 'expert-analysis'"
        ref="tag"
        class="text-xs leading-[1.125rem] text-alert-error-stroke font-bold"
        :class="tagClass"
      >
        {{ tag }}
      </p>

      <!-- eslint-disable vue/no-v-html -->
      <template v-if="title">
        <nuxt-link
          v-if="hasLink"
          :to="trailingSlash(redirectUrl || url)"
          :target="openInNewTab? '_blank': '_self'"
        >
          <p
            ref="title"
            class="font-bold w-full relative"
            :class="component.title"
            v-html="title"
          >
          </p>
        </nuxt-link>

        <p
          v-else
          ref="title"
          class="font-bold w-full relative break-all"
          :class="component.title"
          v-html="title"
        >
        </p>
      </template>

      <p
        v-if="(content && layout !== 'small-tile') && showContent && type !== 'perks'"
        ref="content"
        class="content"
        :class="component.content"
        v-html="content"
      >
      </p>

      <p
        v-if="layout !== 'small-tile' && showAuthor && authorName && type !== 'perks'"
        ref="author-name"
        class="text-sm author-name block mt-2"
      >
        by

        <nuxt-link
          :to="trailingSlash(prefixAuthorUrl + authorUrl)"
          :target="openInNewTab? '_blank': '_self'"
        >
          <strong class="underline">{{ authorName }}</strong>
        </nuxt-link>
      </p>

      <div
        v-if="layout !== 'small-tile'"
        class="flex"
        :class="component.buttonWrapper"
      >
        <atoms-button
          v-if="buttonConfig.show"
          :class="[
            component.button,
            (buttonConfig && buttonConfig.class || '')
          ]"
          v-bind="buttonConfig.props"
          has-icon
          @click="handleClick"
        >
          {{ buttonConfig.text }}
        </atoms-button>

        <p
          v-if="type === 'perks'"
          :class="{
            'ml-auto': layout === 'horizontal'
          }"
          class="text-[0.625rem] leading-4 text-gray-80"
        >
          T&Cs apply
        </p>
      </div>
    </div>
  </article>
</template>

<script setup>

import { isValidUrl } from '~/helpers/validator'

import {
  DEFAULT_AUTHOR_URL
} from '~/constants/pages/contentful'

defineOptions({
  name: 'MoleculesPostTile'
})

const props = defineProps({
  dataTestid: {
    type: String,
    default: 'post-tile'
  },

  layout: {
    type: String,
    default: 'horizontal',
    validator: value => ['horizontal', 'horizontal-small', 'vertical', 'small-tile'].includes(value)
  },

  type: {
    type: String,
    default: 'expert-analysis',
    validator: value => ['expert-analysis', 'perks', 'sidebar'].includes(value)
  },

  title: {
    type: String,
    default: ''
  },

  tag: {
    type: String,
    default: ''
  },

  image: {
    type: String,
    default: FEATURED_IMAGE_DEFAULT_IMAGE,
    validator: value => isValidUrl(value)
  },

  imageAlt: {
    type: String,
    default: ''
  },

  logo: {
    type: String,
    default: ''
  },

  logoAlt: {
    type: String,
    default: ''
  },

  imageLazyLoad: {
    type: Boolean,
    default: false
  },

  content: {
    type: String,
    default: ''
  },

  authorName: {
    type: String,
    default: ''
  },

  authorUrl: {
    type: String,
    default: ''
  },

  prefixAuthorUrl: {
    type: String,
    default: DEFAULT_AUTHOR_URL
  },

  url: {
    type: String,
    default: ''
  },

  redirectUrl: {
    type: String,
    default: '' 
  },

  buttonConfig: {
    type: Object,
    default: () => {
      return {
        show: false,
        text: 'Button Text',
        props: {
          theme: 'secondary',
          size: 'small'
        },
        events: {
          click: event => event
        }
      }
    }
  },

  hasBorder: {
    type: Boolean,
    default: false
  },

  borderClass: {
    type: String,
    default: ''
  },

  hasDivider: {
    type: [Boolean, Number],
    default: false
  },

  dividerClass: {
    type: String,
    default: ''
  },

  tileHeight: {
    type: [String, Number],
    default: ''
  },

  showContent: {
    type: Boolean,
    default: true
  },

  showAuthor: {
    type: Boolean,
    default: true
  },

  tagClass: {
    type: [String, Array],
    default: ''
  },
  openInNewTab: {
    type: Boolean,
    default: false
  },

  verticalAlign: {
    type: Boolean,
    default: false
  }
})

const hasLink = computed(() => ['expert-analysis', 'sidebar'].includes(props.type))

const component = computed(() => {
  const layout = props.layout
  const type = props.type

  const hasDivider = props.hasDivider
  const dividerClass = props.dividerClass

  const hasBorder = props.hasBorder
  const borderClass = props.borderClass

  const main = (() => {
    let common = getKey(layout, {
      horizontal: `flex-col md:flex-row ${getKey(type, {
        perks: 'bg-white md:rounded-lg md:overflow-hidden'
      })}`,
      'horizontal-small': '!flex-row',
      vertical: `w-full ${getKey(type, {
        'expert-analysis': 'md:flex-col',
        perks: 'bg-white md:flex-col h-[8.5rem] md:h-auto min-w[16rem] md:min-w-min'
      })}`,
      'small-tile': 'py-3 first:pt-0 gap-4'
    })

    if (hasDivider && !dividerClass) {
      if (layout === 'small-tile') {
        common += ' border-b-1 border-b-gray-400 overflow-hidden last:border-b-0'
      } else {
        common += `
          relative before:hidden md:before:block before:content-[''] before:bg-gray-400
          before:absolute
          before:-bottom-[0.5625rem] md:before:bottom-[unset] md:before:right-[-1rem]
          before:w-full before:h-[0.0625rem]
          md:before:h-full md:before:w-[0.0625rem]
        `
      }
    } else if (hasDivider && dividerClass) {
      common += ` ${dividerClass}`
    }

    if (hasBorder && !borderClass) {
      if (layout === 'small-tile') {
        common += 'border-1 border-gray-400 rounded-lg overflow-hidden'
      } else {
        common += ' md:border-1 md:border-gray-400 md:rounded-lg overflow-hidden'
      }
    } else if (hasBorder && borderClass) {
      common += ` ${borderClass}`
    }

    return common
  })()

  const image = getKey(layout, {
    'horizontal': {
      class: getKey(type, {
        default: '',
        'expert-analysis': 'rounded-lg w-full h-full max-h-[13.75rem] md:max-h-[13.125rem] md:max-w-[24rem] md:min-w-[24rem]',
        perks: 'h-full rounded-lg md:rounded-none max-h-[11.5rem] md:max-h-[initial] w-full md:min-w-[16rem] md:max-w-[16rem]'
      }),
      width: getKey(type, {
        default: 384,
        perks: 1000
      }),
      height: getKey(type, {
        default: 210,
        perks: 500
      }),
      sizes: getKey(type, {
        'expert-analysis': '382px md:608px md:384px',
        perks: '382px sm:608px md:256px'
      })
    },

    'horizontal-small': {
      class: getKey(type, {
        default: 'rounded-lg w-full h-full min-w-[5.25rem] max-h-[5.25rem] md:max-h-[5.25rem] md:min-h-[6.5rem] md:max-w-[6.5rem] md:min-w-[6.5rem]' 
      }),
      width: getKey(type, {
        default: 104
      }),
      height: getKey(type, {
        default: 104
      })
    },

    vertical: {
      class: `object-cover w-full ${getKey(type, {
        default: '',
        'expert-analysis': removeClassExtras(`
          rounded-[0.25rem] md:rounded-lg
          w-full h-full
          min-h-[5.25rem] max-h-[5.25rem] 
          md:min-h-[13.125rem] md:max-h-[13.125rem] 
          lg:min-h-[9.6875rem] lg:max-h-[9.6875rem]
          min-w-[5.25rem] max-w-[5.25rem] md:max-w-unset
        `),
        perks: 'w-full min-w-[8rem] max-w-[8rem] h-[8rem] min-h-full md:max-w-full xl:min-w-[16rem]'
      })}`,
      width: getKey(type, {
        default: 384,
        perks: 1000
      }),
      height: getKey(type, {
        default: 210,
        perks: 500
      }),
      sizes: getKey(type, {
        'expert-analysis': '84px md:352px lg:224px xl:256px',
        perks: '128px lg:222px xl:256px'
      })
    },

    'small-tile': {
      class: 'hidden md:block rounded-md md:min-w-[3rem] md:max-w-[3rem] md:min-h-[3rem] md:max-h-[3rem]',
      width: 150,
      height: 150
    }
  })

  const title = getKey(layout, {
    horizontal: getKey(type, {
      'expert-analysis': 'text-xl md:text-2xl',
      perks: 'text-base md:text-lg'
    }),
    'horizontal-small': 'text-base md:text-lg',
    vertical: getKey(type, {
      'expert-analysis': 'text-base md:text-xl',
      perks: 'text-sm'
    }),
    'small-tile': 'text-semantic-sizing-label-lg'
  })

  const details = getKey(type, {
    'expert-analysis, sidebar': `gap-1 md:gap-2 ${getKey(layout, {
      'horizontal': 'pt-4 md:pt-0 md:px-6',
      'horizontal-small': 'md:!gap-1 pl-4',
      vertical: 'pl-4 md:pl-0 md:pt-4',
      'small-tile': 'justify-center'
    })}`,
    perks: `gap-4 ${getKey(layout, {
      'horizontal, horizontal-small': 'pt-4 md:pl-8 md:pr-6 md:py-6 justify-center',
      vertical: 'p-4 justify-between'
    })}`
  })

  const content = getKey(layout, {
    'horizontal': getKey(type, {
      'expert-analysis': 'text-sm md:text-base',
      perks: 'text-base'
    }),
    'horizontal-small': 'text-xs md:text-sm',
    vertical: getKey(type, {
      'expert-analysis': 'text-xs leading-[1.125rem] md:text-base break-words w-full',
      perks: 'text-sm'
    }),
    'small-tile': ''
  })

  const buttonWrapper = getKey(layout, {
    'horizontal, horizontal-small': 'w-full md:justify-between items-center',
    vertical: 'w-full flex-col gap-2'
  })

  const button = getKey(layout, {
    vertical: getKey(type, {
      perks: 'sm:text-[0.6875rem] md:text-sm max-w-[12.375rem] md:max-w-none px-3'
    })
  })

  return {
    main,
    image,
    title,
    details,
    content,
    buttonWrapper,
    button
  }
})
</script>
